import {
  getInvokeChannel
} from "./chunk-QL34RJ4Y.js";
import {
  createRendererTransport,
  getOID,
  isMain,
  trustedIpcMain
} from "./chunk-3HMZ6FL6.js";
import {
  isRenderer
} from "./chunk-A6K3NLMV.js";
import {
  __require
} from "./chunk-DGUM43GV.js";

// src/lib/InterProcessEvents.ts
import { EventEmitter } from "eventemitter3";

// src/utils/webcontents.ts
var isWebContentsValid = (webContents) => {
  return !!webContents && !webContents.isDestroyed() && !webContents.isCrashed();
};

// src/lib/InterProcessEvents.ts
var stripInvokeTransportPrefix = (handler) => (_ipcEvent, _originId, ...args) => handler(...args);
var fanoutTally = null;
function startIpcFanoutTally() {
  fanoutTally = /* @__PURE__ */ new Map();
}
function stopIpcFanoutTally() {
  fanoutTally = null;
}
function drainIpcFanoutTally() {
  if (!fanoutTally) {
    return null;
  }
  const drained = fanoutTally;
  fanoutTally = /* @__PURE__ */ new Map();
  return drained;
}
function recordFanoutFrame(namespace, event, args) {
  if (!fanoutTally) {
    return;
  }
  let source = `${namespace}:${event}`;
  if (event === "mutate") {
    const op = args[0];
    if (op?.key) source = `${namespace}:${op.key}`;
  } else if (event === "mutate.batch") {
    const ops = args[0];
    const firstKey = ops?.[0]?.key;
    source = `${namespace}:batch${firstKey ? `:${firstKey}` : ""}`;
  }
  fanoutTally.set(source, (fanoutTally.get(source) ?? 0) + 1);
}
var InterProcessEvents = class extends EventEmitter {
  _namespace;
  _ipcChannel;
  _processId;
  _ipcTransport;
  /**
   * Replies to targeted invokes (`invokeOn`) main is still waiting on. Such a
   * reply is for main alone, so it is consumed here instead of fanned out to
   * every renderer. Broadcast invokes keep their fan-out.
   */
  _pendingInvokeResponses = /* @__PURE__ */ new Set();
  constructor(namespace, channel) {
    super();
    this._namespace = namespace;
    this._processId = getOID();
    this._ipcChannel = channel ?? "events";
    if (isRenderer) {
      if (typeof window.ipc?.channels !== "undefined") {
        this._ipcTransport = window.ipc.channels[this._ipcChannel];
      } else {
        this._ipcTransport = createRendererTransport(this._ipcChannel);
      }
      if (!this._ipcTransport) {
        throw new Error(`Error resolving ipc transport for channel: ${this._ipcChannel}`);
      }
      this._ipcTransport.on(`${this._namespace}`, this._handleEvent);
    } else {
      if (!isMain) {
        console.debug("Not running in electron main process, skipping ipcMain call");
        return;
      }
      trustedIpcMain.on(
        `${this._ipcChannel}:${this._namespace}`,
        (_event, originId, name, ...args) => {
          this._handleEvent(originId, name, ...args);
        }
      );
    }
  }
  /**
   * Override default event emission logic.
   * Emit events to all renderers and to the Main process.
   *
   * @param event
   * @param args
   */
  // @ts-ignore
  emit = (event, ...args) => {
    super.emit(event, ...args);
    this._emitFromOrigin(event, this._processId, ...args);
  };
  /**
   * Emit an event to other processes, excluding self.
   * @param event
   * @param args
   */
  emitToPeers = (event, ...args) => {
    this._emitFromOrigin(event, this._processId, ...args);
  };
  /**
   * Emit an event across the active process only.
   * @param event
   * @param args
   */
  emitLocally = (event, ...args) => {
    super.emit(event, ...args);
  };
  /**
   * Emit an event to all other processes.
   *
   * If the event originates from the Main process, the event is emitted to
   * the {@link Electron.WebContents|webContents} of every {@link Electron.BrowserWindow} instance.
   *
   * If the event originates from the renderer, the event is emitted to the Main process.
   * The Main process then propagates the event to all other Renderers.
   *
   * @param event
   * @param originId
   * @param args
   * @private
   */
  _emitFromOrigin = (event, originId, ...args) => {
    if (!this.crossesProcesses(event)) return;
    if (isRenderer) {
      this._ipcTransport.send(`${this._namespace}`, originId, event, ...args);
    } else {
      if (!isMain) {
        console.warn("Not running in electron main process, skipping emit from origin");
        return;
      }
      recordFanoutFrame(this._namespace, event, args);
      const { BrowserWindow, webContents } = __require("electron");
      (BrowserWindow.getAllWindows() || []).forEach((browserWindow) => {
        if (browserWindow && !browserWindow.isDestroyed() && isWebContentsValid(browserWindow?.webContents)) {
          browserWindow?.webContents?.send(`${this._ipcChannel}:${this._namespace}`, originId, event, ...args);
        } else {
          let id;
          if (browserWindow && !browserWindow.isDestroyed() && browserWindow.webContents && !browserWindow.webContents.isDestroyed()) {
            id = browserWindow?.webContents?.id;
          }
          console.warn("Cannot emit to window, webContents INVALID:", id);
        }
      });
      const webviewContents = webContents.getAllWebContents().filter((wc) => wc.getType() === "webview");
      webviewContents.forEach((webviewContents2) => {
        if (!isWebContentsValid(webviewContents2)) {
          console.warn("Cannot emit to webview, webContents INVALID:", webviewContents2?.id);
          return;
        }
        if (webviewContents2.getURL().includes("highlightai.com") || webviewContents2.getURL().includes("http://localhost")) {
          webviewContents2.send(`${this._ipcChannel}:${this._namespace}`, originId, event, ...args);
        }
      });
    }
  };
  /** Subclasses return false for events that must stay in the process that emitted them. */
  crossesProcesses(_event) {
    return true;
  }
  _handleEvent = (originId, event, ...args) => {
    if (originId === this._processId || !this.crossesProcesses(event)) {
      return;
    }
    if (isRenderer) {
      super.emit(event, ...args);
    } else if (this._pendingInvokeResponses.has(event)) {
      super.emit(event, ...args);
    } else {
      super.emit(event, ...args);
      this._emitFromOrigin(event, originId, ...args);
    }
  };
  /**
   * Invoke IPC handlers across all processes.
   * Allows main to call invoke and a renderer to handle the invocation.
   * @param event
   * @param args
   */
  invoke = async (event, ...args) => {
    if (isRenderer) {
      return this._ipcTransport?.invoke(getInvokeChannel(this._namespace, event), this._processId, ...args);
    }
    return this._invokeFromMain(event, args);
  };
  invokeWithTimeout = async (event, timeoutMs, ...args) => {
    if (isRenderer) {
      let timeout = null;
      try {
        return await Promise.race([
          this.invoke(event, ...args),
          new Promise((_, reject) => {
            timeout = setTimeout(
              () => reject(new Error(`Timed out invoking "${event}" after ${timeoutMs}ms`)),
              timeoutMs
            );
          })
        ]);
      } finally {
        if (timeout) clearTimeout(timeout);
      }
    }
    return this._invokeFromMain(event, args, timeoutMs);
  };
  /**
   * Main only. Invoke a handler in one specific renderer instead of every
   * renderer and whitelisted webview. Use this whenever the request or the
   * reply carries something only that renderer should see.
   */
  invokeOn = async (target, event, timeoutMs, ...args) => {
    if (isRenderer) {
      throw new Error("invokeOn is only available in the main process");
    }
    return this._invokeFromMain(event, args, timeoutMs, target);
  };
  async _invokeFromMain(event, args, timeoutMs, target) {
    const crypto = await import("crypto");
    const invokeId = crypto.randomUUID();
    const responseEvent = `invoke:${event}:${invokeId}`;
    return new Promise((resolve, reject) => {
      let timeout = null;
      const cleanup = () => {
        this._pendingInvokeResponses.delete(responseEvent);
        this.off(responseEvent, onResponse);
        if (timeout) clearTimeout(timeout);
      };
      const onResponse = (result) => {
        cleanup();
        const error = this._invokeError(result);
        if (error) {
          const err = new Error(error.message);
          err.stack = error.stack;
          console.error("ERROR INVOKING:", err);
          reject(err);
          return;
        }
        resolve(result);
      };
      if (target) {
        this._pendingInvokeResponses.add(responseEvent);
      }
      this.on(responseEvent, onResponse);
      if (timeoutMs !== void 0) {
        timeout = setTimeout(() => {
          cleanup();
          reject(new Error(`Timed out invoking "${event}" after ${timeoutMs}ms`));
        }, timeoutMs);
      }
      if (target) {
        if (!isWebContentsValid(target)) {
          cleanup();
          reject(new Error(`Cannot invoke "${event}": target webContents is gone`));
          return;
        }
        target.send(`${this._ipcChannel}:${this._namespace}`, this._processId, `invoke:${event}`, invokeId, ...args);
        return;
      }
      this.emit(`invoke:${event}`, invokeId, ...args ?? []);
    });
  }
  _invokeError(result) {
    if (!result || typeof result !== "object" || !("error" in result)) return null;
    const error = result.error;
    if (!error || typeof error !== "object") return null;
    return error;
  }
  /**
   * Handle IPC invocations across all processes.
   * Allows main to call .invoke and a renderer to handle the invocation.
   *
   * The handler signature is identical in every process: it receives only the
   * invoke payload arguments. (On main, `ipcMain.handle` prepends
   * `(ipcEvent, originId)` to every renderer invoke — that transport prefix
   * is stripped here so call sites never see the positional contract.)
   * @param event
   * @param handler
   */
  handle(event, handler) {
    if (isRenderer) {
      this.on(`invoke:${event}`, async (invokeId, ...args) => {
        try {
          const result = await handler(...args);
          this.emit(`invoke:${event}:${invokeId}`, result ?? void 0);
        } catch (error) {
          console.error("ERROR HANDLING INVOCATION:", event, error);
          this.emit(`invoke:${event}:${invokeId}`, {
            error: {
              // @ts-ignore
              stack: error.stack,
              // @ts-ignore
              message: error.message
            }
          });
        }
      });
    } else {
      if (!isMain) {
        console.debug("Not running in electron main process, skipping ipcMain call");
        return;
      }
      return trustedIpcMain.handle(
        `${this._ipcChannel}:${getInvokeChannel(this._namespace, event)}`,
        stripInvokeTransportPrefix(handler)
      );
    }
  }
};

// src/lib/StoreSyncManager.ts
var SYNC_TIMEOUT_MS = 1e4;
var BATCH_SYNC_TIMEOUT_MS = 15e3;
var MAX_SYNC_RETRIES = 3;
var StoreSyncManagerClass = class {
  registry = /* @__PURE__ */ new Map();
  batchSyncPromise = null;
  batchSyncResolver = null;
  mainHandlersRegistered = false;
  batchSyncCompleted = false;
  /**
   * Register a store for sync management.
   * If batch sync has already completed, triggers individual sync for late-registered stores.
   */
  register(store2) {
    this.registry.set(store2._namespace, store2);
    if (!isRenderer) {
      if (!this.mainHandlersRegistered) {
        this.mainHandlersRegistered = true;
        this.setupBatchSyncHandler(store2);
      }
      this.setupStoreHandlers(store2);
    }
    if (isRenderer && this.batchSyncCompleted) {
      this.syncStoreWithMain(store2).catch((err) => {
        console.error(`Failed to sync late-registered store ${store2._namespace}:`, err);
      });
    }
  }
  /**
   * Set up batch sync handler on the first store.
   * Batch sync uses the first store consistently on both sides.
   */
  setupBatchSyncHandler(store2) {
    if (isRenderer) {
      throw new Error("Cannot call setupBatchSyncHandler from outside of a main process");
    }
    store2.on("renderer:batchSyncWithMain", (pid, namespaces) => {
      const allStates = {};
      for (const ns of namespaces) {
        const registeredStore = this.registry.get(ns);
        if (registeredStore) {
          allStates[ns] = registeredStore.rendererState();
        }
      }
      store2.emit(`main:batchSyncWithRenderer:${pid}`, allStates);
    });
  }
  /**
   * Set up individual sync handlers for a specific store.
   * Each store needs its own handlers because each has its own IPC channel/namespace.
   */
  setupStoreHandlers(store2) {
    if (isRenderer) {
      throw new Error("Cannot call setupStoreHandlers from outside of a main process");
    }
    const crypto = __require("crypto");
    store2.on("renderer:syncWithMain", (pid) => {
      store2.emit(`main:syncWithRenderer:${pid}`, store2.rendererState());
    });
    store2.on("renderer:verifyState", (pid, namespace, hash) => {
      try {
        const sha1 = crypto.createHash("sha1").update(JSON.stringify(store2.rendererState())).digest("hex");
        const verified = sha1 === hash;
        store2.emit(`main:verifyState:${pid}`, verified);
      } catch (err) {
        console.error(`Error in verifyState handler for pid=${pid}, namespace=${namespace}:`, err);
        store2.emit(`main:verifyState:${pid}`, false);
      }
    });
  }
  /**
   * Sync a single renderer store with main process (used for late-registered stores).
   * @param store - The store to sync
   * @param retryCount - Current retry attempt (internal use)
   */
  async syncStoreWithMain(store2, retryCount = 0) {
    if (!isRenderer) {
      throw new Error("Cannot call syncStoreWithMain from outside of a renderer process");
    }
    try {
      const { sha1Renderer } = await import("./hash.renderer-MZ3XJELE.js");
      await new Promise((resolve, reject) => {
        let timeoutId = null;
        let verifyTimeoutId = null;
        let settled = false;
        const clearTimers = () => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          if (verifyTimeoutId) {
            clearTimeout(verifyTimeoutId);
            verifyTimeoutId = null;
          }
        };
        timeoutId = setTimeout(() => {
          if (settled) return;
          settled = true;
          clearTimers();
          reject(new Error(`Timeout waiting for syncWithMain response for ${store2._namespace}`));
        }, SYNC_TIMEOUT_MS);
        store2.once(`main:syncWithRenderer:${store2._processId}`, async (state2) => {
          if (settled) return;
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          store2._state = { ...state2 };
          verifyTimeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            clearTimers();
            reject(new Error(`Timeout waiting for verifyState response for ${store2._namespace}`));
          }, SYNC_TIMEOUT_MS);
          store2.once(`main:verifyState:${store2._processId}`, (verified) => {
            if (settled) return;
            settled = true;
            clearTimers();
            if (!verified) {
              if (retryCount < MAX_SYNC_RETRIES) {
                setTimeout(() => {
                  console.log(
                    `Reverifying ${store2._namespace} state... (attempt ${retryCount + 2}/${MAX_SYNC_RETRIES + 1})`
                  );
                  this.syncStoreWithMain(store2, retryCount + 1).then(resolve).catch(reject);
                }, 1e3);
              } else {
                console.error(
                  `${store2._namespace} state verification failed after ${MAX_SYNC_RETRIES + 1} attempts, giving up`
                );
                store2._ready = true;
                store2.emit("renderer:ready");
                resolve();
              }
              return;
            }
            store2._ready = true;
            store2.emit("renderer:ready");
            console.log(`${store2._namespace} state verified!`);
            resolve();
          });
          try {
            store2.emit(
              "renderer:verifyState",
              store2._processId,
              store2._namespace,
              await sha1Renderer(JSON.stringify(store2.all()))
            );
          } catch (err) {
            if (settled) return;
            settled = true;
            clearTimers();
            reject(err);
          }
        });
        store2.emit("renderer:syncWithMain", store2._processId, store2._namespace);
      });
    } catch (err) {
      console.error(`Error in syncStoreWithMain for ${store2._namespace}:`, err);
      if (retryCount < MAX_SYNC_RETRIES) {
        console.log(
          `Retrying syncStoreWithMain for ${store2._namespace} (attempt ${retryCount + 2}/${MAX_SYNC_RETRIES + 1})`
        );
        await new Promise((resolve) => setTimeout(resolve, 1e3));
        return this.syncStoreWithMain(store2, retryCount + 1);
      }
      console.error(`syncStoreWithMain for ${store2._namespace} failed after ${MAX_SYNC_RETRIES + 1} attempts`);
      store2._ready = true;
      store2.emit("renderer:ready");
      throw err;
    }
  }
  /**
   * Batch sync all registered renderer stores with a single IPC round-trip.
   * Uses preloaded initial store data from window.__INITIAL_STORE_DATA when available,
   * then runs background verification and only re-syncs stores that fail verification.
   */
  async batchSyncWithMain() {
    if (!isRenderer) {
      throw new Error("Cannot call batchSyncWithMain from outside of a renderer process");
    }
    if (this.batchSyncPromise) {
      return this.batchSyncPromise;
    }
    const initialPromise = typeof window !== "undefined" && window.__INITIAL_STORE_DATA;
    if (initialPromise && typeof initialPromise.then === "function") {
      try {
        const data = await initialPromise;
        if (data && typeof data === "object" && Object.keys(data).length > 0) {
          for (const [namespace, store2] of this.registry) {
            const state2 = data[namespace] ?? {};
            this.applyStateToStore(store2, state2);
          }
          this.batchSyncCompleted = true;
          this.batchSyncPromise = Promise.resolve();
          queueMicrotask(() => {
            for (const store2 of this.registry.values()) {
              this.verifyStoreInBackground(store2);
            }
          });
          console.log("Preloaded store data successfully");
          return;
        }
      } catch (err) {
        console.warn("Preloaded store data failed, falling back to batch sync:", err);
      }
    }
    this.batchSyncPromise = new Promise((resolve) => {
      this.batchSyncResolver = resolve;
    });
    const namespaces = Array.from(this.registry.keys());
    const stores = Array.from(this.registry.values());
    const firstStore = stores[0];
    if (!firstStore) {
      this.batchSyncResolver?.();
      this.batchSyncPromise = null;
      return;
    }
    let timeoutId = null;
    let settled = false;
    const clearTimer = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
    const finalizeBatchSync = (success) => {
      if (settled) return;
      settled = true;
      clearTimer();
      this.batchSyncCompleted = true;
      this.batchSyncResolver?.();
      this.batchSyncResolver = null;
      this.batchSyncPromise = null;
      if (success) {
        queueMicrotask(() => {
          for (const store2 of this.registry.values()) {
            this.verifyStoreInBackground(store2);
          }
        });
      }
    };
    try {
      timeoutId = setTimeout(() => {
        if (settled) return;
        console.error(`Timeout waiting for batchSyncWithMain response after ${BATCH_SYNC_TIMEOUT_MS}ms`);
        for (const store2 of this.registry.values()) {
          if (!store2._ready) {
            store2._ready = true;
            store2.emitLocally("renderer:ready");
          }
        }
        finalizeBatchSync(false);
      }, BATCH_SYNC_TIMEOUT_MS);
      firstStore.once(`main:batchSyncWithRenderer:${firstStore._processId}`, (allStates) => {
        if (settled) return;
        try {
          for (const [namespace, store2] of this.registry) {
            const state2 = allStates[namespace] ?? {};
            this.applyStateToStore(store2, state2);
          }
          finalizeBatchSync(true);
        } catch (err) {
          console.error("Error applying batch sync states:", err);
          for (const store2 of this.registry.values()) {
            if (!store2._ready) {
              store2._ready = true;
              store2.emitLocally("renderer:ready");
            }
          }
          finalizeBatchSync(false);
        }
      });
      firstStore.emit("renderer:batchSyncWithMain", firstStore._processId, namespaces);
    } catch (err) {
      console.error("Error in batchSyncWithMain:", err);
      for (const store2 of this.registry.values()) {
        if (!store2._ready) {
          store2._ready = true;
          store2.emitLocally("renderer:ready");
        }
      }
      finalizeBatchSync(false);
      throw err;
    }
    return this.batchSyncPromise;
  }
  /**
   * Apply state to a store without triggering sync events.
   */
  applyStateToStore(store2, state2) {
    store2._state = { ...state2 };
    store2._ready = true;
    store2.emitLocally("renderer:ready");
  }
  /**
   * Run background verification for a store after sync.
   * Ensures state integrity without blocking the ready state.
   */
  async verifyStoreInBackground(store2) {
    if (!isRenderer || store2._verificationPending) {
      return;
    }
    store2._verificationPending = true;
    try {
      const { sha1Renderer } = await import("./hash.renderer-MZ3XJELE.js");
      const hash = await sha1Renderer(JSON.stringify(store2.all()));
      let timeoutId = null;
      let handled = false;
      timeoutId = setTimeout(() => {
        if (handled) return;
        handled = true;
        store2._verificationPending = false;
        console.warn(`Timeout waiting for verifyState response for ${store2._namespace}, triggering re-sync...`);
        this.syncStoreWithMain(store2).catch((err) => {
          console.error(`Failed to re-sync ${store2._namespace} after verification timeout:`, err);
        });
      }, SYNC_TIMEOUT_MS);
      store2.once(`main:verifyState:${store2._processId}`, (verified) => {
        if (handled) return;
        handled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        store2._verificationPending = false;
        if (!verified) {
          console.warn(`${store2._namespace} state verification failed, re-syncing...`);
          this.syncStoreWithMain(store2).catch((err) => {
            console.error(`Failed to re-sync ${store2._namespace} after verification failure:`, err);
          });
        } else {
          console.debug(`${store2._namespace} state verified!`);
        }
      });
      store2.emit("renderer:verifyState", store2._processId, store2._namespace, hash);
    } catch (err) {
      store2._verificationPending = false;
      console.error(`Error verifying ${store2._namespace} state:`, err);
    }
  }
  /**
   * Check if batch sync has completed and all stores are ready.
   */
  isBatchSyncReady() {
    return this.batchSyncPromise === null && Array.from(this.registry.values()).every((s) => s._ready);
  }
  /**
   * Check if batch sync has already been performed.
   */
  hasBatchSyncCompleted() {
    return this.batchSyncCompleted;
  }
  /**
   * Returns a snapshot of all registered store states for preloading into a new renderer.
   * Only used in the main process; returns empty object in renderer.
   * Main process should call this when creating a window and pass via IPC or window args.
   */
  getSnapshotForNewRenderer() {
    if (isRenderer) {
      return {};
    }
    const snapshot = {};
    for (const [namespace, store2] of this.registry) {
      snapshot[namespace] = store2.rendererState();
    }
    return snapshot;
  }
};
var StoreSyncManager = new StoreSyncManagerClass();

// src/lib/InterProcessStore.ts
import { getProperty, setProperty, deleteProperty, hasProperty } from "dot-prop";
import deepEqual from "fast-deep-equal";
var STORE_MUTATION_EVENTS = /* @__PURE__ */ new Set(["mutate", "mutate.batch", "set.all", "update"]);
var InterProcessStore = class extends InterProcessEvents {
  _state;
  _ready;
  _verificationPending = false;
  _allowRendererWrites;
  _mainOnly;
  _pendingBatch = null;
  _batchScheduled = false;
  constructor(namespace, options) {
    super(namespace, options?.channel);
    this._state = {};
    this._ready = false;
    this._mainOnly = options?.mainOnly === true;
    if (options?.allowRendererWrites === false || this._mainOnly) {
      this._allowRendererWrites = false;
    }
    this.get = this.get.bind(this);
    this.set = this.set.bind(this);
    this.setIfChanged = this.setIfChanged.bind(this);
    this.setIfChangedInMain = this.setIfChangedInMain.bind(this);
    this.setBatched = this.setBatched.bind(this);
    this.setMapEntry = this.setMapEntry.bind(this);
    this.setMapEntryIfChanged = this.setMapEntryIfChanged.bind(this);
    this.deleteMapEntry = this.deleteMapEntry.bind(this);
    this.appendToArray = this.appendToArray.bind(this);
    this.delete = this.delete.bind(this);
    this.deleteIfPresent = this.deleteIfPresent.bind(this);
    this.setAll = this.setAll.bind(this);
    this.clear = this.clear.bind(this);
    this._flushBatch = this._flushBatch.bind(this);
    this.on("mutate", (op) => {
      this._applyMutation(
        op,
        /* fireLocalListeners */
        true
      );
    });
    this.on("mutate.batch", (ops) => {
      if (!Array.isArray(ops) || ops.length === 0) return;
      for (const op of ops) this._applyMutationStateOnly(op);
      this._emitBatchLocalListeners(ops);
    });
    this.on("set.all", (configs) => {
      this.setAll(configs, false);
    });
    if (!isRenderer) {
      this._ready = true;
      if (!this._mainOnly) {
        this.handle("store:setIfChanged", async (key, value) => this.setIfChanged(key, value));
      }
    }
    StoreSyncManager.register(this);
  }
  /**
   * Sync this store with the main process.
   * Delegates to StoreSyncManager for the actual sync logic.
   */
  async syncWithMain() {
    return StoreSyncManager.syncStoreWithMain(this);
  }
  isReady() {
    return this._ready;
  }
  async whenReady() {
    if (this._ready) {
      return true;
    }
    return await new Promise((resolve) => {
      this.once("renderer:ready", () => resolve(true));
    });
  }
  get(key, defaultValue) {
    if (key?.indexOf(".") !== -1) {
      return getProperty(this._state, key) ?? defaultValue;
    } else {
      return this._state[key] ?? defaultValue;
    }
  }
  /**
   * Get the entire store.
   */
  all() {
    return this._state;
  }
  onMutationApplied() {
  }
  /**
   * Set the value at the specified key in the app configs.
   * Accepts either a value or an updater function like React's setState.
   *
   * IPC: emits exactly one 'mutate' frame (regardless of how nested `key` is).
   *
   * @param key
   * @param valueOrUpdater
   * @param emit
   */
  set(key, valueOrUpdater, emit) {
    if (isRenderer && this._allowRendererWrites === false && emit !== false) {
      throw new Error(`Writing to store "${this._namespace}" disallowed in renderer. key=${key}`);
    }
    const prevValue = this.get(key);
    const value = typeof valueOrUpdater === "function" ? valueOrUpdater(prevValue) : valueOrUpdater;
    const op = { op: "set", key, value };
    this._dropPendingBatchConflicts(key);
    if (emit !== false) {
      this.emit("mutate", op);
    } else {
      this._applyMutation(op, false);
    }
    return value;
  }
  /**
   * Set the value only if it structurally differs from the current value.
   * Every plain `set` broadcasts a structured-cloned payload to all renderers
   * and re-renders every subscriber with fresh object refs — so writers on a
   * polling cadence that often recompute identical values (calendar polls,
   * presence flags, health checks) should use this to skip no-op broadcasts.
   *
   * Equality is a structural deep-compare (key-order insensitive), so it's
   * meant for modest payloads written at polling cadence — not hot-path data.
   *
   * @returns true if the value changed and was written
   */
  setIfChanged(key, value) {
    if (!isRenderer && deepEqual(this.get(key), value)) {
      return false;
    }
    this.set(key, value);
    return true;
  }
  /**
   * Ask the authoritative main-process store to write only when changed.
   * Renderer callers should use this when suppressing main-to-renderer fan-out
   * matters; the normal {@link setIfChanged} remains synchronous.
   */
  async setIfChangedInMain(key, value) {
    if (!isRenderer) {
      return this.setIfChanged(key, value);
    }
    if (this._allowRendererWrites === false) {
      throw new Error(`Writing to store "${this._namespace}" disallowed in renderer. key=${key}`);
    }
    return this.invoke("store:setIfChanged", key, value);
  }
  setBatched(key, valueOrUpdater) {
    if (isRenderer && this._allowRendererWrites === false) {
      throw new Error(`Writing to store "${this._namespace}" disallowed in renderer. key=${key}`);
    }
    const prevValue = this.get(key);
    const value = typeof valueOrUpdater === "function" ? valueOrUpdater(prevValue) : valueOrUpdater;
    this._applyMutationStateOnly({ op: "set", key, value });
    if (!this._pendingBatch) {
      this._pendingBatch = /* @__PURE__ */ new Map();
    }
    this._queuePendingBatchedSet(key, value);
    if (!this._batchScheduled) {
      this._batchScheduled = true;
      queueMicrotask(this._flushBatch);
    }
    return value;
  }
  /**
   * Update a single entry inside a map-shaped value without sending the whole map over IPC.
   */
  setMapEntry(key, mapKey, value, emit) {
    if (isRenderer && this._allowRendererWrites === false && emit !== false) {
      throw new Error(`Writing to store "${this._namespace}" disallowed in renderer. key=${key}`);
    }
    const op = { op: "mapEntry", key, mapKey, value };
    this._dropPendingBatchConflicts(key);
    if (emit !== false) {
      this.emit("mutate", op);
    } else {
      this._applyMutation(op, false);
    }
    return value;
  }
  /**
   * Set a map entry only if it structurally differs from the current entry.
   * The map-entry analog of {@link setIfChanged}: writers that re-derive the
   * full entry on a polling cadence should use this so unchanged entries
   * don't broadcast a no-op frame to every renderer.
   *
   * @returns true if the entry changed and was written
   */
  setMapEntryIfChanged(key, mapKey, value) {
    const existing = this.get(key);
    if (!isRenderer && existing && Object.hasOwn(existing, mapKey) && deepEqual(existing[mapKey], value)) {
      return false;
    }
    this.setMapEntry(key, mapKey, value);
    return true;
  }
  /**
   * Remove a single entry from a map-shaped value. Companion to {@link setMapEntry}.
   * Deleting an absent entry is a full no-op — no frame crosses IPC (the
   * receiving side already ignored such deletes).
   */
  deleteMapEntry(key, mapKey, emit) {
    if (isRenderer && this._allowRendererWrites === false && emit !== false) {
      throw new Error(`Writing to store "${this._namespace}" disallowed in renderer. key=${key}`);
    }
    const existing = this.get(key);
    if (!isRenderer && (!existing || !Object.hasOwn(existing, mapKey))) {
      return;
    }
    const op = { op: "mapDelete", key, mapKey };
    this._dropPendingBatchConflicts(key);
    if (emit !== false) {
      this.emit("mutate", op);
    } else {
      this._applyMutation(op, false);
    }
  }
  /**
   * Append entries to an array-shaped value without sending the whole array over IPC.
   */
  appendToArray(key, ...values) {
    if (isRenderer && this._allowRendererWrites === false) {
      throw new Error(`Writing to store "${this._namespace}" disallowed in renderer. key=${key}`);
    }
    if (values.length === 0) return this.get(key) ?? [];
    const op = { op: "arrayAppend", key, values };
    this._dropPendingBatchConflicts(key);
    this.emit("mutate", op);
    return this.get(key) ?? [];
  }
  increment(key, amount, emit) {
    const current = this.get(key) ?? 0;
    this.set(key, current + amount, emit);
    return current + amount;
  }
  decrement(key, amount, emit) {
    const current = this.get(key) ?? 0;
    this.set(key, current - amount, emit);
    return current - amount;
  }
  /**
   * Delete the value at the specified key in the app configs.
   * @param key
   * @param emit
   */
  delete(key, emit) {
    if (isRenderer && this._allowRendererWrites === false && emit !== false) {
      throw new Error(`Writing to store "${this._namespace}" disallowed in renderer`);
    }
    const op = { op: "delete", key };
    this._dropPendingBatchConflicts(key);
    if (emit !== false) {
      this.emit("mutate", op);
    } else {
      this._applyMutation(op, false);
    }
  }
  /**
   * Delete the key only if it currently holds a value. A plain {@link delete}
   * of an absent key still broadcasts a frame to every renderer, so polling
   * writers that usually find the key already gone should use this instead
   * of a bespoke get-then-delete guard.
   *
   * @returns true if the key existed and was deleted
   */
  deleteIfPresent(key) {
    const exists = key.includes(".") ? hasProperty(this._state, key) : Object.hasOwn(this._state, key);
    if (!isRenderer && !exists) {
      return false;
    }
    this.delete(key);
    return true;
  }
  /**
   * Set all configs for the memory store.
   * @param configs
   * @param emit
   * @returns {*}
   */
  setAll(configs, emit) {
    if (isRenderer && this._allowRendererWrites === false && emit !== false) {
      throw new Error(`Writing to store "${this._namespace}" disallowed in renderer`);
    }
    this._pendingBatch = null;
    this._state = configs;
    this.onMutationApplied();
    if (emit !== false) {
      this.emit("set.all", configs);
      this.emit("update");
    }
    return configs;
  }
  /**
   * Clear the store by resetting it to an empty object via `setAll`.
   */
  clear() {
    return this.setAll({});
  }
  rendererState() {
    return this._mainOnly ? {} : this.all();
  }
  crossesProcesses(event) {
    return !(this._mainOnly && STORE_MUTATION_EVENTS.has(event));
  }
  // ── Internal helpers ──────────────────────────────────────────────
  _keysOverlap(a, b) {
    return a === b || a.startsWith(`${b}.`) || b.startsWith(`${a}.`);
  }
  _dropPendingBatchConflicts(key) {
    if (!this._pendingBatch || this._pendingBatch.size === 0) return;
    for (const pendingKey of this._pendingBatch.keys()) {
      if (this._keysOverlap(pendingKey, key)) {
        this._pendingBatch.delete(pendingKey);
      }
    }
    if (this._pendingBatch.size === 0) {
      this._pendingBatch = null;
    }
  }
  _queuePendingBatchedSet(key, value) {
    if (!this._pendingBatch) return;
    for (const pendingKey of this._pendingBatch.keys()) {
      if (pendingKey === key) {
        this._pendingBatch.set(key, value);
        return;
      }
      if (key.startsWith(`${pendingKey}.`)) {
        this._pendingBatch.set(pendingKey, this.get(pendingKey));
        return;
      }
      if (pendingKey.startsWith(`${key}.`)) {
        this._pendingBatch.delete(pendingKey);
      }
    }
    this._pendingBatch.set(key, value);
  }
  _applyMutationStateOnly(op) {
    switch (op.op) {
      case "set": {
        if (op.key.indexOf(".") === -1) {
          this._state[op.key] = op.value;
        } else {
          setProperty(this._state, op.key, op.value);
        }
        this.onMutationApplied();
        return;
      }
      case "delete": {
        deleteProperty(this._state, op.key);
        this.onMutationApplied();
        return;
      }
      case "mapEntry": {
        const existing = this.get(op.key) ?? {};
        const next = { ...existing, [op.mapKey]: op.value };
        if (op.key.indexOf(".") === -1) {
          this._state[op.key] = next;
        } else {
          setProperty(this._state, op.key, next);
        }
        this.onMutationApplied();
        return;
      }
      case "mapDelete": {
        const existing = this.get(op.key) ?? {};
        if (!Object.hasOwn(existing, op.mapKey)) return;
        const next = { ...existing };
        delete next[op.mapKey];
        if (op.key.indexOf(".") === -1) {
          this._state[op.key] = next;
        } else {
          setProperty(this._state, op.key, next);
        }
        this.onMutationApplied();
        return;
      }
      case "arrayAppend": {
        const existing = this.get(op.key) ?? [];
        const next = existing.concat(op.values);
        if (op.key.indexOf(".") === -1) {
          this._state[op.key] = next;
        } else {
          setProperty(this._state, op.key, next);
        }
        this.onMutationApplied();
        return;
      }
    }
  }
  _applyMutation(op, fireLocalListeners) {
    this._applyMutationStateOnly(op);
    if (!fireLocalListeners) return;
    this._emitLocalListeners(op.key, this._leafValueFor(op));
  }
  _leafValueFor(op) {
    switch (op.op) {
      case "set":
        return op.value;
      case "delete":
        return void 0;
      case "mapEntry":
      case "mapDelete":
      case "arrayAppend":
        return this.get(op.key);
    }
  }
  _emitLocalListeners(key, leafValue) {
    this.emitLocally(key, leafValue);
    if (key.indexOf(".") === -1) return;
    const parts = key.split(".");
    for (let i = 1; i < parts.length; i++) {
      const k = parts.slice(0, i).join(".");
      if (this.listenerCount(k) > 0) {
        this.emitLocally(k, this.get(k));
      }
    }
  }
  _emitBatchLocalListeners(ops) {
    if (ops.length === 1) {
      this._emitLocalListeners(ops[0].key, this._leafValueFor(ops[0]));
      return;
    }
    const leafKeys = /* @__PURE__ */ new Set();
    for (const op of ops) {
      if (leafKeys.has(op.key)) continue;
      leafKeys.add(op.key);
      this.emitLocally(op.key, this._leafValueFor(op));
    }
    const parentKeys = /* @__PURE__ */ new Set();
    for (const op of ops) {
      if (op.key.indexOf(".") === -1) continue;
      const parts = op.key.split(".");
      for (let i = 1; i < parts.length; i++) {
        const k = parts.slice(0, i).join(".");
        if (leafKeys.has(k) || parentKeys.has(k)) continue;
        parentKeys.add(k);
      }
    }
    for (const k of parentKeys) {
      if (this.listenerCount(k) > 0) {
        this.emitLocally(k, this.get(k));
      }
    }
  }
  /**
   * Flush the queued setBatched ops as a single IPC frame. State was already
   * applied at setBatched-time; this just fires deferred local listeners
   * (with parent-keyspace dedupe) and sends the wire frame.
   */
  _flushBatch() {
    this._batchScheduled = false;
    const pendingBatch = this._pendingBatch;
    this._pendingBatch = null;
    if (!pendingBatch || pendingBatch.size === 0) return;
    const batch = Array.from(pendingBatch, ([key, value]) => ({ op: "set", key, value }));
    this._emitBatchLocalListeners(batch);
    if (batch.length === 1) {
      this.emitToPeers("mutate", batch[0]);
    } else {
      this.emitToPeers("mutate.batch", batch);
    }
  }
};

// src/lib/EncryptedJsonStore.ts
var EncryptedJsonStore = class extends InterProcessStore {
  _loaded;
  _name;
  _saveTimeout;
  _options;
  lastPendingWrite = 0;
  lastWrite = 0;
  _pendingSave = null;
  _resolveSave = null;
  constructor(name, options) {
    if (!name) {
      throw new Error(`Missing required 'name' initializing GlobalStore`);
    }
    super(`${name}.store`, {
      channel: options?.channel,
      mainOnly: options?.mainOnly
    });
    this._loaded = false;
    this._name = name;
    this._saveTimeout = void 0;
    this._options = options ?? {};
  }
  getName = () => {
    return this._name;
  };
  onMutationApplied() {
    if (!isRenderer) {
      this.lastPendingWrite = Date.now();
      if (this._loaded) {
        const pendingSave = this.save(100);
        if (pendingSave) {
          pendingSave.catch((err) => {
            console.error(`Error scheduling ${this._name} store save:`, err);
          });
        }
      }
    }
  }
  /**
   * Get the absolute path of the store file.
   * @return string
   */
  storeFile = () => {
    if (isRenderer) {
      return null;
    }
    if (!isMain) {
      console.warn("Not running in electron main process, skipping storeFile call");
      return null;
    }
    const path = __require("path");
    const { app } = __require("electron");
    const args = [app.getPath("userData"), "store"];
    if (this._options?.subDirectoryName?.length) {
      args.push(this._options.subDirectoryName);
    }
    return path.join(...args, this._name, `store.json`);
  };
  /**
   * Override whenReady function from InterProcessStore.
   * We need to ensure the load function was called when calling inside the main process.
   */
  whenReady = async () => {
    if (isRenderer) {
      return super.whenReady();
    }
    if (this._loaded) {
      return true;
    }
    return new Promise((resolve) => {
      this.once("loaded", () => resolve(true));
    });
  };
  /**
   * In the main process, this loads the store from disk if the file exists and is an
   * encrypted store.json. Then, it subscribes to updates to the store and performs a
   * debounced save on each update.
   *
   * In the renderer process, this waits for the renderer to receive the store contents
   * from the main process.
   */
  load = async () => {
    if (this._loaded) {
      return this;
    }
    if (isRenderer) {
      await this.whenReady();
      this._loaded = true;
    } else {
      try {
        const storeFile = this.storeFile();
        const fs = __require("fs-extra");
        const storeFileExists = await fs.pathExists(storeFile);
        if (!storeFileExists) {
          console.log(`Store file does not exist yet: ${storeFile}`);
          this._loaded = true;
          this.emit("loaded");
          return this;
        }
        const encrypted = await fs.readJson(storeFile);
        if (!encrypted.i || !encrypted.d) {
          console.warn(`Store file is empty, or not yet encrypted: ${storeFile}, ${JSON.stringify(encrypted)}`);
          this._loaded = true;
          this.emit("loaded");
          return this;
        }
        if (!isRenderer) {
          const { decryptStore } = await import("./JsonCrypto.main-FNDHWDHC.js");
          const store2 = await decryptStore(this._name, encrypted);
          this.setAll(store2);
        }
        this._loaded = true;
        this.emit("loaded");
      } catch (err) {
        console.error("Error loading encrypted json store:", err);
      }
    }
    return this;
  };
  write = async () => {
    if (isRenderer) {
      return;
    }
    const lastPendingWrite = this.lastPendingWrite;
    const storeFile = this.storeFile();
    const { encryptStore } = await import("./JsonCrypto.main-FNDHWDHC.js");
    const encrypted = await encryptStore(this._name, this.all());
    try {
      const fs = __require("fs-extra");
      await fs.ensureFile(storeFile);
      if (!isRenderer) {
        const { writeFileAsync } = await import("./write-atomic-file-SOFI4IP5.js");
        await writeFileAsync(storeFile, JSON.stringify(encrypted));
        this.lastWrite = lastPendingWrite;
      }
    } catch (err) {
      console.error(`Error writing to ${this._name} store:`, err);
    } finally {
      this._saveTimeout = void 0;
    }
  };
  /**
   * Debounced save method that uses a shared promise (_pendingSave).
   * If debounce > 0 the write is scheduled, otherwise writes immediately.
   */
  save = async (debounce = 100) => {
    if (isRenderer) {
      return;
    }
    if (this._saveTimeout) {
      clearTimeout(this._saveTimeout);
      this._saveTimeout = void 0;
    }
    if (!this._pendingSave) {
      this._pendingSave = new Promise((resolve) => {
        this._resolveSave = resolve;
      });
    }
    if (debounce > 0) {
      this._saveTimeout = setTimeout(async () => {
        try {
          await this.write();
        } catch (err) {
          console.error("Error during debounced write:", err);
        } finally {
          if (this._resolveSave) {
            this._resolveSave();
          }
          this._pendingSave = null;
          this._resolveSave = null;
        }
      }, debounce);
      return this._pendingSave;
    } else {
      try {
        await this.write();
      } catch (err) {
        console.error("Error during immediate write:", err);
      } finally {
        if (this._resolveSave) {
          this._resolveSave();
        }
        this._pendingSave = null;
        this._resolveSave = null;
      }
    }
  };
  /**
   * afterWrite ensures that all pending changes are saved to disk.
   *
   * The key logic is:
   * - Check if lastPendingWrite > lastWrite. If yes, there are unsaved changes.
   * - If no write is currently pending (_pendingSave is null and _writing is false), force an immediate write.
   * - Otherwise, wait for the shared pending promise to resolve.
   */
  afterWrite = async () => {
    if (isRenderer) return;
    let hadNewWrite;
    do {
      if (this._pendingSave) {
        await this._pendingSave;
      }
      if (this.lastPendingWrite > this.lastWrite) {
        await this.save(0);
      }
      await new Promise((resolve) => setTimeout(resolve, 150));
      hadNewWrite = this.lastPendingWrite > this.lastWrite;
    } while (hadNewWrite);
  };
};

// src/lib/impl/GlobalState.ts
var GlobalStateInstance = class extends InterProcessStore {
  constructor() {
    super("global.state");
  }
};
var state = new GlobalStateInstance();
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window._GlobalState = state;
}
var GlobalState = state;

// src/lib/impl/GlobalStore.ts
var GlobalStoreInstance = class {
  stores;
  constructor() {
    this.stores = {
      // Name of apps-preferences store doesn't match key, that's OK -- can't change it without migrating old store name.
      "apps-preferences": new EncryptedJsonStore("app-preferences"),
      "app-state": new EncryptedJsonStore("app-state"),
      settings: new EncryptedJsonStore("settings"),
      windows: new EncryptedJsonStore("windows"),
      general: new EncryptedJsonStore("general"),
      // welcome to the general store 𐚁
      user: new EncryptedJsonStore("user"),
      superpowers: new EncryptedJsonStore("superpowers"),
      facts: new EncryptedJsonStore("facts"),
      conversations: new EncryptedJsonStore("conversations"),
      telemetry: new EncryptedJsonStore("telemetry"),
      // Holds the WorkOS access and refresh tokens; renderers get the access token from the auth router.
      workos: new EncryptedJsonStore("workos", { mainOnly: true }),
      device: new EncryptedJsonStore("device"),
      chat: new EncryptedJsonStore("chat"),
      flags: new EncryptedJsonStore("flags"),
      billing: new EncryptedJsonStore("billing")
    };
  }
  /**
   * Returns an {@link EncryptedJsonStore} by the specified `storeName`.
   * @param storeName
   */
  access = (storeName) => {
    return this.stores[storeName];
  };
  /**
   * Returns an array of all of the {@link EncryptedJsonStore}s.
   */
  allStores = () => {
    return Object.values(this.stores);
  };
  clearAll = () => {
    Object.values(this.stores).forEach((store2) => {
      if (isRenderer && store2._mainOnly) return;
      store2.clear();
    });
  };
  /**
   * Awaits all stores finishing loading.
   * If this is called in the renderer, this is the same as calling `store.whenReady`.
   */
  loadStores = async () => {
    return await Promise.all(this.allStores().map((store2) => store2.load()));
  };
  /**
   * Returns true if every store is ready in the renderer.
   * In the main process, this will always return true.
   */
  isReady = () => {
    return this.allStores().every((store2) => store2.isReady());
  };
  /**
   * Awaits all stores finishing writing to disk.
   * In ths renderer process, this is a no-op.
   */
  afterWrite = async () => {
    return await Promise.all(this.allStores().map((store2) => store2.afterWrite()));
  };
  // Shorthand getters for stores
  appsPreferences = () => this.stores["apps-preferences"];
  appState = () => this.stores["app-state"];
  settings = () => this.stores.settings;
  general = () => this.stores.general;
  user = () => this.stores.user;
  superpowers = () => this.stores.superpowers;
  facts = () => this.stores.facts;
  windows = () => this.stores.windows;
  conversations = () => this.stores.conversations;
  telemetry = () => this.stores.telemetry;
  workos = () => this.stores.workos;
  device = () => this.stores.device;
  chat = () => this.stores.chat;
  flags = () => this.stores.flags;
  billing = () => this.stores.billing;
};
var store = new GlobalStoreInstance();
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  window._GlobalStore = store;
}
var GlobalStore = store;

export {
  stripInvokeTransportPrefix,
  startIpcFanoutTally,
  stopIpcFanoutTally,
  drainIpcFanoutTally,
  InterProcessEvents,
  StoreSyncManager,
  InterProcessStore,
  EncryptedJsonStore,
  GlobalState,
  GlobalStore
};
//# sourceMappingURL=chunk-XVNHYOIL.js.map